import { useState } from "react";
import { api, IMG } from "@/lib/api";
import { Loader2, MessageCircle, ArrowUpRight, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function DealRoom() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/dealroom/join", form);
      if (res.data.success) {
        setWhatsappUrl(res.data.whatsapp_url);
        toast.success("You're in. Welcome to the Deal Room.");
      } else {
        toast.error(res.data.message || "Something went wrong");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="dealroom-page">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img src={IMG.interior1} alt="Deal Room" className="absolute inset-0 w-full h-full object-cover img-warm" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
        <div className="relative h-full container-edge flex items-end pb-16">
          <div className="text-white max-w-3xl">
            <p className="overline text-[#e8c79a]">Private · Agents only</p>
            <h1 className="hero-headline mt-4 text-5xl md:text-6xl lg:text-7xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              The Deal Room.
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl font-light">
              A private WhatsApp group where LA's top agents share off-market listings,
              events, and resources you won't find on the MLS.
            </p>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">What's inside</p>
            <h2 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              The room you wish existed.
            </h2>
            <p className="mt-6 text-soft leading-relaxed">
              Real conversations, real leads, real deals. The Deal Room is a curated WhatsApp
              circle for LA agents who'd rather collaborate than compete.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pl-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              { t: "Off-market listings", d: "Pocket listings and coming-soon properties shared in real time." },
              { t: "Industry events", d: "Mixers, broker tours, and exclusive previews — first to know." },
              { t: "Vetted resources", d: "Lenders, stagers, photographers, escrow officers the room actually uses." },
              { t: "Deal-making talk", d: "Negotiation wins, contract puzzles, and field-tested scripts." },
            ].map((b, i) => (
              <div key={b.t} className="border-l-2 border-soft pl-6" data-testid={`dealroom-benefit-${i}`}>
                <h4 style={{ fontFamily: "Playfair Display, serif" }} className="text-xl">{b.t}</h4>
                <p className="mt-3 text-soft text-sm leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join form */}
      <section className="section bg-linen" data-testid="dealroom-join">
        <div className="max-w-2xl mx-auto bg-paper border border-soft p-10 md:p-14">
          {whatsappUrl ? (
            <div className="text-center" data-testid="dealroom-success">
              <CheckCircle2 size={36} className="text-sand mx-auto" strokeWidth={1.25} />
              <p className="overline mt-6">You're in</p>
              <h2 className="mt-3 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
                Welcome to the room.
              </h2>
              <p className="mt-4 text-soft">
                Tap the button below to join the private WhatsApp group. See you in there.
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-8 inline-flex"
                data-testid="dealroom-whatsapp-link"
              >
                <MessageCircle size={16} className="mr-2" /> Open WhatsApp group
                <ArrowUpRight size={14} className="ml-2" />
              </a>
              <p className="mt-6 text-xs text-soft">
                Heads up — the room is moderated. Please introduce yourself when you join.
              </p>
            </div>
          ) : (
            <>
              <Lock size={28} className="text-sand" strokeWidth={1.25} />
              <p className="overline mt-6">Request access</p>
              <h2 className="mt-3 text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
                Join the Deal Room.
              </h2>
              <p className="mt-4 text-soft text-sm">
                Drop your name and email. You'll get the private WhatsApp link instantly.
              </p>
              <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="dealroom-form">
                <div>
                  <label className="overline">Name</label>
                  <input
                    className="input-editorial mt-2"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Your full name"
                    data-testid="dealroom-name"
                    required
                  />
                </div>
                <div>
                  <label className="overline">Email</label>
                  <input
                    type="email"
                    className="input-editorial mt-2"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@brokerage.com"
                    data-testid="dealroom-email"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="dealroom-submit"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin mr-2" /> Sending invite…</>
                  ) : (
                    <><MessageCircle size={16} className="mr-2" /> Get my WhatsApp invite</>
                  )}
                </button>
              </form>
              <p className="mt-6 text-xs text-soft">
                The Deal Room is for active LA real estate professionals. Sonia reviews
                memberships periodically. No spam — just deals.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
